import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EditIcon, useToast } from "@signet/ui";
import { savePhoneNote, saveNote, setRemoved, shortDate, stockKeys, type Stock } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Notes on accounts and phones, and the 3 reason ban's "Removed from phone"
   (Jay, 2026-10-22).

   A note is one free-text box per account and per phone, for whatever the
   poster needs to remember ("password reset 20 Oct", "screen cracked").

   A 3 reason ban has to be logged out of the phone it was on, and someone
   presses a button to say it was. Until then the account asks for it
   wherever the phone is shown.
   ────────────────────────────────────────────────────────────────────────── */

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

/** A 3 reason ban still waiting to be logged out of its phone. Graveyard accounts are off the phone views already. */
export const needsRemoving = (a: Stock) => a.status === "banned" && a.banReason === "three_reasons" && !a.removedOn && !a.offPhone && Boolean(a.phone);

/**
 * The note, and an Edit that turns it into a box with Save and Cancel.
 * empty: what to say when there is no note, or null to say nothing.
 * A phone keeps its button in its header (PhoneNoteButton), so it passes
 * button={false} and says itself when the box is open.
 */
export function NoteEditor({ note, canEdit, label, onSave, empty = "No note yet.", button = true, editing: openFromOutside, onEditing }: {
  note: string | null; canEdit: boolean; label: string; onSave: (note: string) => Promise<unknown>; empty?: string | null;
  button?: boolean; editing?: boolean; onEditing?: (editing: boolean) => void;
}) {
  const [openHere, setOpenHere] = useState(false);
  const editing = openFromOutside ?? openHere;
  const setEditing = (next: boolean) => (onEditing ? onEditing(next) : setOpenHere(next));
  const [value, setValue] = useState(note ?? "");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  useEffect(() => {
    if (!editing) setValue(note ?? "");
  }, [note, editing]);

  if (!editing) {
    if (!button && !note) return null;
    return (
      <div className="igs-notebox">
        {note ? <p className="igs-notebox-text">{note}</p> : empty ? <p className="faint">{empty}</p> : null}
        {canEdit && button ? (
          <button type="button" className={note || empty ? "secondary-button" : "text-button"} onClick={() => setEditing(true)} aria-label={`${note ? "Edit" : "Add"} the note on ${label}`}>
            <EditIcon size={13} /> {note ? "Edit note" : "Add note"}
          </button>
        ) : null}
      </div>
    );
  }
  const save = async () => {
    setBusy(true);
    try {
      await onSave(value);
      setEditing(false);
    } catch (error) {
      toast.error("Couldn't save the note", messageOf(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="igs-notebox editing">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={3}
        maxLength={2000}
        autoFocus
        aria-label={`Note on ${label}`}
        placeholder="Anything to remember about it"
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void save();
          if (e.key === "Escape") {
            e.stopPropagation();
            setEditing(false);
          }
        }}
      />
      <div className="igs-notebox-actions">
        <button type="button" className="text-button" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
        <button type="button" className="button brass" onClick={() => void save()} disabled={busy || value.trim() === (note ?? "").trim()}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

export function AccountNote({ a, canEdit }: { a: Stock; canEdit: boolean }) {
  const client = useQueryClient();
  return (
    <NoteEditor
      note={a.note}
      canEdit={canEdit}
      label={`@${a.username}`}
      onSave={async (note) => {
        await saveNote(a.id, note);
        await client.invalidateQueries({ queryKey: stockKeys.all });
      }}
    />
  );
}

/** Add note, or Edit note once there is one: on a phone's header line right of its type chip, and on each account line (Jay, 2026-10-22). */
export function NoteButton({ label, note, onClick }: { label: string; note: string | null; onClick: () => void }) {
  return (
    <button type="button" className="text-button igs-phone-note-button" onClick={onClick} aria-label={`${note ? "Edit" : "Add"} the note on ${label}`}>
      <EditIcon size={13} /> {note ? "Edit note" : "Add note"}
    </button>
  );
}

export const PhoneNoteButton = ({ phone, note, onClick }: { phone: string; note: string | null; onClick: () => void }) => <NoteButton label={`phone ${phone}`} note={note} onClick={onClick} />;

/** An account line's note box, opened by its NoteButton; the note itself already shows on the line. */
export function AccountNoteBox({ a, onDone }: { a: Stock; onDone: () => void }) {
  const client = useQueryClient();
  return (
    <div className="igs-row-notebox">
      <NoteEditor
        note={a.note}
        canEdit
        label={`@${a.username}`}
        button={false}
        editing
        onEditing={(open) => (open ? undefined : onDone())}
        onSave={async (note) => {
          await saveNote(a.id, note);
          await client.invalidateQueries({ queryKey: stockKeys.all });
        }}
      />
    </div>
  );
}

/** The phone's note under its header, and the box once its header button is pressed. Nothing when neither. */
export function PhoneNote({ poster, phone, note, canEdit, editing, onEditing }: { poster: string; phone: string; note: string | null; canEdit: boolean; editing: boolean; onEditing: (editing: boolean) => void }) {
  const client = useQueryClient();
  if (!note && !editing) return null;
  return (
    <div className="igs-phone-note">
      <NoteEditor
        note={note}
        canEdit={canEdit}
        label={`phone ${phone}`}
        empty={null}
        button={false}
        editing={editing}
        onEditing={onEditing}
        onSave={async (text) => {
          await savePhoneNote({ poster, phone, note: text });
          await client.invalidateQueries({ queryKey: stockKeys.all });
        }}
      />
    </div>
  );
}

export function useRemoved() {
  const client = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, removed }: { id: string; removed: boolean }) => setRemoved(id, removed),
    onSuccess: () => client.invalidateQueries({ queryKey: stockKeys.all }),
    onError: (error) => toast.error("Couldn't save that", messageOf(error)),
  });
}

/** The red ask on a 3 reason ban until it is logged out, then the day it was. undo only where there is room for it (the drawer). */
export function RemovedPrompt({ a, canAct, undo = false }: { a: Stock; canAct: boolean; undo?: boolean }) {
  const removed = useRemoved();
  if (a.removedOn && a.banReason === "three_reasons" && a.status === "banned") {
    return (
      <p className="igs-removed-line">
        Removed from the phone {shortDate(a.removedOn)}.
        {undo && canAct ? (
          <button type="button" className="text-button" disabled={removed.isPending} onClick={() => removed.mutate({ id: a.id, removed: false })}>
            Not yet
          </button>
        ) : null}
      </p>
    );
  }
  if (!needsRemoving(a)) return null;
  return (
    <div className="igs-alert igs-remove">
      <p>
        <strong>3 reason ban: log it out of phone {a.phone}.</strong> Then press Removed from phone.
      </p>
      {canAct ? (
        <button type="button" className="button danger" disabled={removed.isPending} onClick={() => removed.mutate({ id: a.id, removed: true })}>
          Removed from phone
        </button>
      ) : null}
    </div>
  );
}
